/**
 * TikTok Unfurl Client Helper
 * 
 * Client-side utilities for fetching TikTok thumbnails via oEmbed API
 */

/**
 * Normalize TikTok URL by removing trailing slashes and tracking params
 */
export function normalizeTikTokUrl(url: string): string {
  if (!url || typeof url !== 'string') {
    return '';
  }

  let normalized = url.trim();

  // Remove trailing slash
  if (normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }

  // Try to remove common tracking query params while keeping video ID
  try {
    const urlObj = new URL(normalized);
    // Keep only essential params
    const essentialParams = ['v', 'id'];
    const newParams = new URLSearchParams();
    urlObj.searchParams.forEach((value, key) => {
      if (essentialParams.includes(key.toLowerCase())) {
        newParams.set(key, value);
      }
    });
    
    if (newParams.toString()) {
      normalized = `${urlObj.origin}${urlObj.pathname}?${newParams.toString()}`;
    } else {
      normalized = `${urlObj.origin}${urlObj.pathname}`;
    }
  } catch (e) {
    // If URL parsing fails, return trimmed original
    // This is safe - the server will handle validation
  }

  return normalized;
}

/**
 * Check if a URL is likely a TikTok URL
 */
export function isLikelyTikTokUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }
  return url.includes('tiktok.com');
}

/**
 * Fetch TikTok thumbnail and title using oEmbed API
 * 
 * @param url - TikTok URL
 * @returns Object with thumbnailUrl and title (both optional)
 */
export async function unfurlTikTokThumbnail(
  url: string
): Promise<{ thumbnailUrl?: string; title?: string }> {
  if (!url || typeof url !== 'string') {
    return {};
  }

  if (!isLikelyTikTokUrl(url)) {
    return {};
  }

  try {
    const normalizedUrl = normalizeTikTokUrl(url);

    const response = await fetch('/api/unfurl/tiktok', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url: normalizedUrl }),
    });

    if (!response.ok) {
      console.warn('[unfurlTikTokThumbnail] API request failed:', response.status);
      return {};
    }

    const data = await response.json();
    return {
      thumbnailUrl: data.thumbnailUrl || undefined,
      title: data.title || undefined,
    };
  } catch (error) {
    console.error('[unfurlTikTokThumbnail] Error:', error);
    // Never throw - always return empty object
    return {};
  }
}

